import type { TeacherDetail } from '../types/teacherSubmission.types';

const CLASS_NAME_PREFIX = /^Class\s+/i;

interface ExpertiseGridClass {
  id: number;
  shortName: string;
  name: string;
}

interface ExpertiseGridRow {
  subjectId: number;
  subjectName: string;
  isMainSubject: boolean;
  classIds: ReadonlySet<number>;
}

interface ExpertiseGrid {
  classes: ExpertiseGridClass[];
  rows: ExpertiseGridRow[];
}

export function buildExpertiseGrid(teacher: TeacherDetail): ExpertiseGrid {
  const classNameById = new Map<number, string>();
  for (const teacherClass of teacher.classes) classNameById.set(teacherClass.id, teacherClass.name);

  const subjectNameById = new Map<number, string>();
  const classIdsBySubjectId = new Map<number, Set<number>>();
  for (const expertise of teacher.subjectExpertise) {
    classNameById.set(expertise.classId, expertise.className);
    for (const subject of expertise.subjects) {
      subjectNameById.set(subject.id, subject.name);
      const classIds = classIdsBySubjectId.get(subject.id) ?? new Set<number>();
      classIds.add(expertise.classId);
      classIdsBySubjectId.set(subject.id, classIds);
    }
  }

  const mainSubjectIds = new Set(teacher.mainSubjects.map((subject) => subject.id));
  for (const subject of teacher.mainSubjects) subjectNameById.set(subject.id, subject.name);

  const classes = [...classNameById].map(([id, name]) => ({
    id,
    name,
    shortName: name.replace(CLASS_NAME_PREFIX, ''),
  }));

  const rows = [...subjectNameById]
    .map(([subjectId, subjectName]) => ({
      subjectId,
      subjectName,
      isMainSubject: mainSubjectIds.has(subjectId),
      classIds: classIdsBySubjectId.get(subjectId) ?? new Set<number>(),
    }))
    .toSorted(
      (left, right) =>
        Number(right.isMainSubject) - Number(left.isMainSubject) ||
        left.subjectName.localeCompare(right.subjectName),
    );

  return { classes, rows };
}
